/**
 * Browser Authorization-Code + PKCE client for the separate-origin Driver
 * ePickup app (CLAUDE.md #6: native/external surfaces auth via OIDC
 * Authorization Code + PKCE — never embed a secret in the binary).
 *
 * This is net-new: the internal AFFiNE frontend is cookie-session based and has
 * no reusable browser PKCE client. The driver backend `/api/driver/*` is
 * Bearer-only, so the driver app mints its own access token via public PKCE (no
 * client secret) and sends it as `Authorization: Bearer`.
 */

export interface DriverAuthConfig {
  /** Keycloak realm issuer, e.g. https://auth.pipe.localhost/realms/pipe */
  issuer: string;
  /** public client id (no secret) */
  clientId: string;
  /** window.location.origin + "/callback" */
  redirectUri: string;
  /** requested scopes, e.g. "openid driver" */
  scope: string;
}

const DEFAULT_ISSUER = 'https://auth.pipe.localhost/realms/pipe';

/**
 * Runtime config: an env-injected global wins (so a deployment can repoint the
 * issuer without a rebuild), otherwise the localhost default.
 */
export function driverAuthConfig(): DriverAuthConfig {
  const injected =
    (globalThis as { DRIVER_AUTH_ISSUER?: string }).DRIVER_AUTH_ISSUER ??
    DEFAULT_ISSUER;
  return {
    issuer: injected,
    clientId: 'pipe-driver',
    redirectUri: window.location.origin + '/callback',
    scope: 'openid driver offline_access',
  };
}

const VERIFIER_KEY = 'pipe.driver.pkce.verifier';
const STATE_KEY = 'pipe.driver.pkce.state';
const TOKEN_KEY = 'pipe.driver.access_token';

/** base64url encode raw bytes (no padding, URL-safe alphabet). */
function base64UrlEncode(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) {
    str += String.fromCharCode(b);
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Generate a 43-character (256-bit) base64url code verifier per RFC 7636
 * (length 43..128). Uses the CSPRNG `crypto.getRandomValues`.
 */
export function generateCodeVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** Random opaque state for CSRF protection of the redirect round-trip. */
export function generateState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** base64url(sha256(verifier)) — the S256 PKCE code challenge. */
export async function codeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Build the Keycloak authorization-endpoint URL for the redirect.
 * response_type=code, S256 challenge, no secret.
 */
export function buildAuthorizeUrl(
  cfg: DriverAuthConfig,
  challenge: string,
  state: string
): string {
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: cfg.scope,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });
  return `${cfg.issuer}/protocol/openid-connect/auth?${params.toString()}`;
}

/**
 * Exchange the authorization code for an access token at the token endpoint.
 * Public PKCE: grant_type=authorization_code + code_verifier, NO client_secret.
 */
export async function exchangeCode(
  cfg: DriverAuthConfig,
  code: string,
  verifier: string
): Promise<{ access_token: string; refresh_token?: string; expires_in?: number }> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    redirect_uri: cfg.redirectUri,
    client_id: cfg.clientId,
    code_verifier: verifier,
  });
  const res = await fetch(`${cfg.issuer}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`driver token exchange failed: ${res.status}`);
  }
  return (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
  };
}

/**
 * Thrown by {@link refreshAccessToken} on a non-2xx token response. `authFailure` distinguishes a
 * GENUINELY DEAD refresh token (Keycloak 4xx `invalid_grant`/`invalid_token` — the RT must be
 * discarded) from a TRANSIENT server failure (5xx — the RT is still valid, retry later). A network/
 * offline failure makes `fetch` reject BEFORE this is thrown, so it surfaces as a plain error (not a
 * `TokenRefreshError`) — also transient. The biometric-resume boot keys off this to avoid destroying a
 * valid Keystore RT (and forcing a network-dependent re-login) on an offline cold-launch.
 */
export class TokenRefreshError extends Error {
  readonly authFailure: boolean;
  constructor(public readonly status: number) {
    super(`driver token refresh failed: ${status}`);
    this.name = 'TokenRefreshError';
    this.authFailure = status >= 400 && status < 500;
  }
}

/**
 * Refresh-token grant: exchange a stored refresh token for a fresh access token.
 * Public PKCE client — client_id, NO client_secret.
 */
export async function refreshAccessToken(
  cfg: DriverAuthConfig,
  refreshToken: string
): Promise<{ access_token: string; refresh_token?: string; expires_in?: number }> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: cfg.clientId,
  });
  const res = await fetch(`${cfg.issuer}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new TokenRefreshError(res.status);
  }
  return (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
  };
}

// ---------------------------------------------------------------------------
// Token store (in-memory, mirrored to sessionStorage so a redirect round-trip
// survives the page reload but the token never outlives the browser session).
// ---------------------------------------------------------------------------

let inMemoryToken: string | null = null;

export function getAccessToken(): string | null {
  if (inMemoryToken) {
    return inMemoryToken;
  }
  try {
    inMemoryToken = sessionStorage.getItem(TOKEN_KEY);
  } catch {
    inMemoryToken = null;
  }
  return inMemoryToken;
}

export function setAccessToken(token: string): void {
  inMemoryToken = token;
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    // sessionStorage may be unavailable (private mode); in-memory still works.
  }
}

export function logout(): void {
  inMemoryToken = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Begin the login redirect: mint a verifier+state, stash them, and send the
 * browser to Keycloak. Does not return (assigns window.location).
 */
export async function login(
  cfg: DriverAuthConfig = driverAuthConfig()
): Promise<void> {
  const verifier = generateCodeVerifier();
  const state = generateState();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  const challenge = await codeChallenge(verifier);
  window.location.assign(buildAuthorizeUrl(cfg, challenge, state));
}

/**
 * Handle the `/callback` redirect: validate state, exchange the code, store the
 * access token. Returns `{ ok, refreshToken? }` — the refresh token is SURFACED
 * (not stored here) so the native provider can persist it behind the Keystore
 * biometric gate (D6). On web the caller discards it (no long-lived secret on web).
 */
export async function handleCallback(
  cfg: DriverAuthConfig = driverAuthConfig()
): Promise<{ ok: boolean; refreshToken?: string }> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');
  const returnedState = url.searchParams.get('state');
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!code || !verifier || returnedState !== expectedState) {
    return { ok: false };
  }
  const { access_token, refresh_token } = await exchangeCode(cfg, code, verifier);
  setAccessToken(access_token);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  return { ok: true, refreshToken: refresh_token };
}
